'use client'

import { useMounted } from '@/lib/utils/utils-client'
import React, { useEffect, useRef, useState } from 'react'
import { FaAngleLeft, FaAngleRight } from 'react-icons/fa6'

/**
 * Carousel Component
 * ---------------------------------------------------------------------------------------------------------------------
 */
export default function Carousel(props: any) {

  const refContainer = useRef<any>(null)

  const [activeIndex, setActiveIndex] = useState<number>(0)

  const ROLLING_DELAY_MS: number = 5000
  const [rollingInterval, setRollingInterval] = useState<any>(null)

  const startRollingInterval = () => {
    clearRollingInterval()
    setRollingInterval(setInterval(async () => {
      setActiveIndex((prevIndex: number) => (prevIndex + 1) % props.children.length)
      await new Promise(resolve => setTimeout(resolve, ROLLING_DELAY_MS))
    }, ROLLING_DELAY_MS))
  }

  const clearRollingInterval = () => {
    clearInterval(rollingInterval)
    setRollingInterval((prevInterval: any) => {
      clearInterval(prevInterval)
      return null
    })
  }

  const setIndex = async (newIndex: number) => {
    setActiveIndex(newIndex)
    startRollingInterval()
  }

  const incrIndex = async () => {
    setActiveIndex((prevIndex: number) => (prevIndex + 1) % props.children.length)
    startRollingInterval()
  }

  const decrIndex = async () => {
    setActiveIndex((prevIndex: number) => ((prevIndex === 0 ? props.children.length : prevIndex) - 1) % props.children.length)
    startRollingInterval()
  }

  useEffect(() => {
    const itemWidth = refContainer.current?.children[0].offsetWidth
    refContainer.current?.scroll({left: (props.fullWidth ? itemWidth : props.width) * activeIndex, behavior: 'smooth'})
  }, [activeIndex])


  const mounted = useMounted()

  useEffect(() => {
    if (mounted) startRollingInterval()
  }, [mounted])

  useEffect(() => {
    return () => {
      clearRollingInterval()
    }
  }, [])

  // Rendering
  //--------------------------------------------------------------------------------------------------------------------
  return (
    <div id="CAROUSEL"
      className={
        'flex flex-col items-center hideScrollBar overflow-hidden py-4 ' +
           (props.fullWidth ? 'w-full ' : 'p-4 ') +
           (props.className ? props.className : '')
      }>

      <div id="WRAPPER"
        className={'relative w-full shadow-sm hover:shadow-md overflow-hidden ' + (props.fullWidth ? '' : 'rounded-lg')}
        style={{
          width: props.fullWidth ? '100%' : (props.width + 'px'),
          height: props.height + 'px'
        }}>

        <div id="CONTAINER"
          ref={refContainer}
          className={'hideScrollBar flex w-full items-center justify-start overflow-hidden'}>

          <div id="ITEMS"
            className={'flex justify-start items-center ' + (props.fullWidth ? 'w-full ' : '')}>

            {React.Children.map(props.children, (child) => {
              // Checking isValidElement is the safe way and avoids a typescript error too
              if (React.isValidElement(child))
                return React.cloneElement<any>(child, {fullWidth: props.fullWidth, width: props.width, height: props.height})
              return child
            })}

          </div>

        </div>

        <div id="ARROW_LEFT"
          className={'absolute left-0 top-0 flex h-full w-16 cursor-pointer items-center justify-center bg-black/40 text-[50px] font-[600] text-white opacity-0 transition-all hover:opacity-100'}
          onClick={decrIndex}>
          <FaAngleLeft/>
        </div>

        <div id="ARROW_RIGHT"
          className={'absolute right-0 top-0 flex h-full w-16 cursor-pointer items-center justify-center bg-black/40 text-[50px] font-[600] text-white opacity-0 transition-all hover:opacity-100'}
          onClick={incrIndex}>
          <FaAngleRight />
        </div>

      </div>

      <div id="BUTTONS" className={'flex w-full flex-row items-center justify-center p-2'}>
        {props.children.map((child: any, index: any) => (
          <div id="BUTTON"
            key={index}
            className={'h-4 w-4 rounded-full mx-2 my-4 shrink-0 shadow-insetDark ' +
               (activeIndex === index ? 'bg-primary cursor-default' : 'bg-main-lighter/20 hover:bg-main-lightest cursor-pointer')}
            onClick={() => setIndex(index)}/>
        ))}

      </div>

    </div>
  )

}
